import { Container, Group, Skeleton, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { Coverage as CoverageData } from '@nelnela/contracts';
import { api } from '../lib/api';
import { useStudy } from '../app/study';
import { Panel } from './ItemDetail';

/**
 * F15 Coverage — "look what the app learned". Report tone, never a score: the bands say how much
 * of each level band is solid, shaky or unmet, and the wording is careful about the difference
 * between what was MEASURED (fitted from real reviews) and what was ASSUMED (transferred from
 * another axis, or seeded by a lesson). With little data it says so in a sentence instead of
 * drawing a confident chart.
 */
export function Coverage() {
  const nav = useNavigate();
  const { me } = useStudy();
  const profileId = me.profiles[0]?.id ?? '';
  const q = useQuery({
    queryKey: ['coverage', profileId],
    queryFn: () => api.coverage(profileId),
    enabled: !!profileId,
  });

  if (q.isPending) {
    return (
      <Container size={640} py="lg">
        <Skeleton height={26} width="40%" mb={12} />
        <Skeleton height={160} radius={12} />
      </Container>
    );
  }
  if (q.error || !q.data) {
    return (
      <Container size={640} py="lg">
        <Text c="dimmed">Coverage isn’t available yet.</Text>
      </Container>
    );
  }
  const c = q.data;
  const lexeme = c.bands.filter((b) => b.kind === 'lexeme');
  const patterns = c.bands.filter((b) => b.kind === 'pattern' || b.kind === 'grammar_point');
  const measured = lexeme.reduce((n, b) => n + b.solidFitted, 0);

  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        <UnstyledButton onClick={() => nav(-1)} style={{ fontSize: 12, color: 'var(--nn-muted)' }}>
          ← back
        </UnstyledButton>
        <Title order={2}>Coverage</Title>

        <Panel>
          <Group justify="space-between" align="baseline">
            <Text fz={13} c="dimmed">
              Where you are, roughly
            </Text>
            <Text fz={22} fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {c.estimatedLevel}
            </Text>
          </Group>
          <Text fz={12.5} c="dimmed" mt={6} style={{ lineHeight: 1.55 }}>
            {confidenceLine(c, measured)}
          </Text>
        </Panel>

        <BandChart title="Vocabulary" bands={lexeme} />
        {patterns.length > 0 && <BandChart title="Patterns and grammar" bands={patterns} />}

        {c.weakestPatterns.length > 0 && (
          <Panel>
            <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
              Shakiest patterns
            </Text>
            <Group gap={8} mt={7}>
              {c.weakestPatterns.map((p) => (
                <Link
                  key={p.id}
                  to={`/words/item/${p.id}`}
                  style={{ fontSize: 12.5, color: 'var(--nn-link)' }}
                  lang="ka"
                >
                  {p.displayForm ?? p.lemma}
                </Link>
              ))}
            </Group>
          </Panel>
        )}

        {c.confusables.length > 0 && (
          <Panel>
            <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
              Pairs you mix up
            </Text>
            <Stack gap={4} mt={7}>
              {c.confusables.map((p, i) => (
                <Text key={i} fz={12.5} lang="ka">
                  {p.a.displayForm ?? p.a.lemma} <span style={{ color: 'var(--nn-faint)' }}>↔</span>{' '}
                  {p.b.displayForm ?? p.b.lemma}
                </Text>
              ))}
            </Stack>
          </Panel>
        )}
      </Stack>
    </Container>
  );
}

function confidenceLine(c: CoverageData, measured: number): string {
  if (c.confidence === 'low' && measured === 0) {
    return 'Almost nothing has been measured yet — keep watching and this will fill in on its own. Until then this number is your own estimate, not the app’s.';
  }
  if (c.confidence === 'low') {
    return `Based on ${measured} words the app has actually seen you handle. Early days: treat it as a direction, not a verdict.`;
  }
  if (c.confidence === 'medium') {
    return `Based on ${measured} measured words, plus some carried over from other skills (shown paler below).`;
  }
  return `Based on ${measured} words measured from what you did — not from anything you told us.`;
}

/** One row per level band: a stacked bar of solid / assumed / shaky / unmet. */
function BandChart({ title, bands }: { title: string; bands: CoverageData['bands'] }) {
  if (bands.length === 0) return null;
  const byBand = new Map<number, CoverageData['bands'][number]>();
  for (const b of bands) {
    const prev = byBand.get(b.levelBandStart);
    byBand.set(
      b.levelBandStart,
      prev
        ? {
            ...prev,
            itemsInBand: prev.itemsInBand + b.itemsInBand,
            solidFitted: prev.solidFitted + b.solidFitted,
            solidAssumed: prev.solidAssumed + b.solidAssumed,
            shaky: prev.shaky + b.shaky,
            unmet: prev.unmet + b.unmet,
          }
        : b,
    );
  }
  const rows = [...byBand.values()].sort((a, b) => a.levelBandStart - b.levelBandStart);
  return (
    <Panel>
      <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }} mb={9}>
        {title}
      </Text>
      <Stack gap={7}>
        {rows.map((b) => {
          const total = Math.max(1, b.itemsInBand);
          const pct = (n: number) => `${(n / total) * 100}%`;
          return (
            <Group key={b.levelBandStart} gap={9} wrap="nowrap">
              <Text
                fz={11}
                c="dimmed"
                style={{ width: 46, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
              >
                {b.levelBandStart}–{b.levelBandStart + 9}
              </Text>
              <div
                style={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  overflow: 'hidden',
                  display: 'flex',
                  background: 'var(--nn-hairline)',
                }}
                aria-label={`${b.solidFitted} solid, ${b.solidAssumed} assumed, ${b.shaky} shaky, ${b.unmet} unmet`}
              >
                <div style={{ width: pct(b.solidFitted), background: 'var(--nn-accent)' }} />
                <div
                  style={{
                    width: pct(b.solidAssumed),
                    background: 'var(--nn-accent)',
                    opacity: 0.45,
                  }}
                />
                <div style={{ width: pct(b.shaky), background: 'var(--nn-muted)', opacity: 0.5 }} />
              </div>
              <Text
                fz={11}
                c="dimmed"
                style={{ width: 54, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              >
                {b.solidFitted + b.solidAssumed}/{b.itemsInBand}
              </Text>
            </Group>
          );
        })}
      </Stack>
      <Text fz={10.5} c="dimmed" mt={9}>
        solid · <span style={{ opacity: 0.6 }}>assumed from another skill</span> · shaky · the rest
        unmet
      </Text>
    </Panel>
  );
}
